import React, { useState, useEffect, useRef } from 'react';
import { Trophy, X, Play, Pause, Maximize, Volume2, VolumeX } from 'lucide-react';

export const AwardBadge = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [tickerHovered, setTickerHovered] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  
  // Swipe-to-close state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 80;

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const handleTickerHover = (e) => {
      setTickerHovered(e.detail.hovered);
    };
    
    window.addEventListener('tickerHover', handleTickerHover);
    return () => window.removeEventListener('tickerHover', handleTickerHover);
  }, []);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isSwipeDown = distance < -minSwipeDistance;
    if (isSwipeDown) {
      handleClosePopup();
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleBadgeClick = () => {
    setShowCelebration(true);
    setTimeout(() => {
      setShowPopup(true);
      setShowCelebration(false);
    }, 800);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleProgressClick = (e) => {
    if (progressRef.current && videoRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      // Support both mouse and touch events
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clickX = clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, clickX / rect.width));
      const newTime = percentage * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleProgressTouch = (e) => {
    e.preventDefault();
    handleProgressClick(e);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Elegant Celebration Effect */}
      {showCelebration && (
        <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden">
          {[...Array(25)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-particle-rise"
              style={{
                right: `${80 + Math.random() * 150}px`,
                bottom: `${100 + Math.random() * 50}px`,
                width: `${4 + Math.random() * 8}px`,
                height: `${4 + Math.random() * 8}px`,
                background: `rgba(255, 255, 255, ${0.4 + Math.random() * 0.4})`,
                boxShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
                animationDelay: `${Math.random() * 0.3}s`,
                animationDuration: `${0.8 + Math.random() * 0.4}s`
              }}
            />
          ))}
          {[...Array(8)].map((_, i) => (
            <div
              key={`line-${i}`}
              className="absolute animate-line-rise"
              style={{
                right: `${100 + Math.random() * 120}px`,
                bottom: `${120 + Math.random() * 30}px`,
                width: '2px',
                height: `${30 + Math.random() * 40}px`,
                background: 'linear-gradient(to top, transparent, rgba(255,255,255,0.6), transparent)',
                animationDelay: `${Math.random() * 0.2}s`,
                animationDuration: `${0.6 + Math.random() * 0.3}s`
              }}
            />
          ))}
          <div className="absolute right-[140px] bottom-[180px] animate-trophy-appear">
            <Trophy className="w-12 h-12 text-white drop-shadow-lg" strokeWidth={1.5} />
          </div>
        </div>
      )}

      {/* Video Popup Modal */}
      {showPopup && (
        <div 
          className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-300"
          onClick={handleClosePopup}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Swipe indicator for mobile */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 md:hidden">
            <div className="w-12 h-1 bg-white/40 rounded-full" />
            <p className="text-white/50 text-xs mt-2 text-center">Nach unten wischen zum Schließen</p>
          </div>

          <div
            className="relative bg-black rounded-xl overflow-hidden shadow-2xl max-w-4xl w-full animate-in zoom-in-95 duration-500 border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleClosePopup}
              className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="bg-black border-b border-white/10 p-5 flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-lg border border-white/10">
                <Trophy className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">1. Platz Deutscher Coworking Preis 2025</h2>
                <p className="text-white/50 text-sm">teamWERK Erolzheim — Nominierungsvideo</p>
              </div>
            </div>

            {/* Video Container */}
            <div className="relative aspect-video bg-black">
              <video
                ref={videoRef}
                className="w-full h-full object-contain"
                autoPlay
                playsInline
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                src="/assets/video/teamwerk-imagevideo.mp4"
              />
              
              {/* Controls Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-3 md:p-4 pb-4 md:pb-5">
                {/* Progress Bar / Timeline - Larger touch target on mobile */}
                <div 
                  ref={progressRef}
                  className="w-full h-3 md:h-1.5 bg-white/20 rounded-full cursor-pointer mb-3 md:mb-4 hover:h-3 md:hover:h-2.5 transition-all touch-none"
                  onClick={handleProgressClick}
                  onTouchStart={handleProgressTouch}
                  onTouchMove={handleProgressTouch}
                >
                  <div 
                    className="h-full bg-white rounded-full relative transition-all"
                    style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                  >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 md:w-3.5 md:h-3.5 bg-white rounded-full shadow-lg md:opacity-0 md:group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                  <button
                    onClick={togglePlay}
                    className="bg-white text-black p-2 md:p-3 rounded-full transition-all hover:scale-105"
                  >
                    {isPlaying ? <Pause className="w-4 h-4 md:w-5 md:h-5" /> : <Play className="w-4 h-4 md:w-5 md:h-5 ml-0.5" />}
                  </button>
                  
                  <button
                    onClick={toggleMute}
                    className="bg-white/10 hover:bg-white/20 text-white p-2 md:p-2.5 rounded-full transition-colors"
                  >
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>

                  {/* Time Display */}
                  <div className="text-white/70 text-xs md:text-sm font-mono">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </div>
                  
                  <div className="flex-grow" />
                  
                  <button
                    onClick={toggleFullscreen}
                    className="bg-white/10 hover:bg-white/20 text-white p-2 md:p-2.5 rounded-full transition-colors"
                  >
                    <Maximize className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Badge - EXACT same size as Event Ticker (200px) */}
      <div 
        className="fixed right-8 z-30 animate-in slide-in-from-bottom-10 fade-in duration-700 cursor-pointer hidden md:block group transition-all ease-[cubic-bezier(0.25,1,0.5,1)]"
        style={{
          bottom: tickerHovered ? '280px' : '112px',
          transitionDuration: '500ms'
        }}
        onClick={handleBadgeClick}
      >
        <div className="relative flex flex-col items-end">
          <span 
            className="absolute inline-flex h-full w-full rounded-2xl bg-gray-400 group-hover:opacity-0 transition-opacity"
            style={{
              animation: 'awardPulse 2.5s ease-in-out infinite'
            }}
          />
          
          {/* Card EXACT same width as Event Ticker: w-[200px] */}
          <div className="relative overflow-hidden bg-black/80 backdrop-blur-md hover:bg-black text-white rounded-2xl shadow-xl border border-white/20 transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105 group-hover:border-white/40 group-hover:shadow-2xl flex flex-col w-[200px]">
            
            {/* Expandable Image - EXACT same height as Event Ticker: h-32 */}
            <div className="h-0 opacity-0 group-hover:h-32 group-hover:opacity-100 transition-all duration-500 ease-in-out w-full overflow-hidden">
              <div className="p-3 flex justify-center bg-gradient-to-b from-gray-800 to-gray-900 h-full items-center relative">
                <img 
                  src="/assets/awards/coworking-preis-2025.png" 
                  alt="Deutscher Coworking Preis 2025" 
                  className="w-full h-auto max-w-[160px] drop-shadow-lg"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-white rounded-full p-2.5 shadow-lg">
                    <Play className="w-4 h-4 text-black fill-black" />
                  </div>
                </div>
              </div>
            </div>

            {/* Content - EXACT same style as Event Ticker */}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="flex-shrink-0 bg-white/10 p-2 rounded-full border border-white/20">
                <Trophy className="h-4 w-4 text-white" />
              </div>
              
              <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex justify-between items-center mb-0.5">
                  <div className="text-[9px] uppercase tracking-widest text-white/70 font-bold">
                    Award
                  </div>
                  <div className="bg-white/20 rounded px-1.5 py-0.5 text-[9px] font-mono text-white border border-white/20">
                    2025
                  </div>
                </div>
                
                <div className="text-xs font-bold text-white leading-tight truncate group-hover:whitespace-normal">
                  1. Platz Coworking Preis
                </div>
                <div className="text-[9px] text-white/60 group-hover:opacity-100 opacity-0 transition-opacity">
                  Video ansehen →
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Badge - Left side, same size as Event Ticker */}
      <div 
        className="fixed left-2 z-30 animate-in slide-in-from-bottom-10 fade-in duration-700 cursor-pointer md:hidden group transition-all"
        style={{
          bottom: '8px',
          transitionDuration: '500ms'
        }}
        onClick={handleBadgeClick}
      >
        <div className="relative">
          {/* Mobile badge - same width as Event Ticker (130px) */}
          <div className="relative overflow-hidden bg-black/90 backdrop-blur-md text-white rounded-xl shadow-lg border border-white/20 transition-all duration-300 w-[130px]">

            <div className="flex items-center gap-2 px-2 py-2">
              <div className="flex-shrink-0 bg-white/10 p-1.5 rounded-full border border-white/20">
                <Trophy className="h-3 w-3 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="text-[8px] uppercase tracking-wider text-white/70 font-bold">
                  Award
                </div>
                <div className="text-[10px] font-bold text-white leading-tight truncate">
                  1. Platz 🏆
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes awardPulse {
          0%, 100% { transform: scale(1); opacity: 0.25; }
          50% { transform: scale(1.06); opacity: 0.45; }
        }
        
        @keyframes particle-rise {
          0% { transform: translateY(0) scale(1); opacity: 0; }
          20% { opacity: 1; }
          100% { transform: translateY(-250px) scale(0.5); opacity: 0; }
        }
        
        @keyframes line-rise {
          0% { transform: translateY(0) scaleY(0); opacity: 0; }
          30% { transform: translateY(-50px) scaleY(1); opacity: 0.8; }
          100% { transform: translateY(-200px) scaleY(0.5); opacity: 0; }
        }
        
        @keyframes trophy-appear {
          0% { transform: scale(0) rotate(-20deg); opacity: 0; }
          50% { transform: scale(1.2) rotate(5deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 0; }
        }
        
        .animate-particle-rise { animation: particle-rise ease-out forwards; }
        .animate-line-rise { animation: line-rise ease-out forwards; }
        .animate-trophy-appear { animation: trophy-appear 0.8s ease-out forwards; }
      `}</style>
    </>
  );
};

export default AwardBadge;
